import { NavigateToNextCellParams, GridApi } from "@ag-grid-enterprise/all-modules";
import cronstrue from "cronstrue";

import { calculateHours, calculateMinutes } from "./periodicUtils";
// import { object, string } from "yup";
import {
    ScheduleDoc,
    NewScheduleState,
    UserInput,
    UserInputType,
    ScheduleType,
    ScheduleFrequency,
    Project,
    ScheduleInfoInput,
    PeriodicSubType
} from "types";
import { TimeRangeOptions, timeWindowOptions } from "components/diagnostics/DiagnosticsMenu";
import { utcToZonedTime, format } from "date-fns-tz";

export const projectKey = "projectId";
export const runKey = "runId";
export const scheduleKey = "scheduleId";
export const diagnosticKey = "logId";
export const rangeKey = "range";
export const policyKey = "policyId";
export const companyKey = "companyId";
export function clearSelection() {
    const doc = document as Document & { selection: { empty: () => void } };
    const body = document.body as HTMLElement & {
        createTextRange(): { collapse: () => void; select: () => void };
    };
    if (body.createTextRange) {
        const range = body.createTextRange();
        range.collapse();
        range.select();
    } else if (window.getSelection) {
        const sel = window.getSelection();
        if (sel) {
            if (sel.empty) {
                sel.empty();
            } else if (sel.removeAllRanges) {
                sel.removeAllRanges();
            }
        }
    } else if (doc.selection) {
        doc.selection.empty();
    }
}

// example for for LLLL format: Tuesday, May 25, 2021 2:50 PM

export const specialCompanyField = "u_companySysId_dom";
export function setInitalCreateScheduleState(
    userInputs: { [key: string]: any },
    existingSchedule?: ScheduleDoc
): NewScheduleState {
    const date = new Date();
    const defaultStartDate = new Date(date.toDateString());
    const mt = date.toLocaleTimeString("en-GB").split(":");
    const defaultStartTime = `${mt[0]}:${mt[1]}`;
    const defaultScheduleTime = "23:59";
    const defaultType = "Once";
    const defaultInteval = 1;
    const cron = parseCron(defaultStartDate, defaultScheduleTime, defaultType);

    return {
        company: { name: "", id: "", ops: false },
        cron: cron,
        scheduleName: "",
        scheduleType: defaultType,
        startDate: defaultStartDate,
        startTime: defaultStartTime,
        scheduleTime: defaultScheduleTime,
        daysOfWeek: new Array<number>(),
        daysOfMonth: new Array<number>(),
        interval: defaultInteval,
        periodic: {
            name: undefined,
            value: undefined
        },
        timeZone: "UTC",
        user_inputs: setInitialUserInputs(userInputs, existingSchedule?.user_input),
        active: true
    };
}

export function setInitialUserInputs(
    userInputs?: { [key: string]: UserInput },
    existingValues?: { [key: string]: UserInput }
) {
    // todo: better way to do this, add all types

    const user_inputs: { [key: string]: UserInput } = {};
    if (userInputs) {
        for (const [key, input] of Object.entries(userInputs)) {
            const t: UserInputType = input.type;
            const existing = existingValues ? existingValues[key] : undefined;
            let initValue: string | string[] | boolean = existing?.value || "";
            let initName = existing?.name || "";
            switch (t) {
                case "listcollector":
                    initValue = [];
                    initName = [];
                    break;
                case "checkbox":
                    initValue = false;
                    initName = input.label;
                    break;
            }
            user_inputs[key] = {
                name: initName,
                value: initValue,
                type: t,
                label: input.label || existing?.label || ""
            };
        }
    }
    return user_inputs;
}

export function parseCron(
    date: Date | Date[] | null | undefined,
    time: string,
    type: ScheduleType,
    daysOfWeek?: any,
    daysOfMonth?: any,
    periodic?: any
): string {
    if (!date || Array.isArray(date)) {
        console.log("date should not null, undefined or an array");
        return "";
    }

    // get hour, minute in the required format
    let [hour, minute] = time.split(":");
    if (hour === "00") {
        hour = "0";
    }
    /* Cron reference

    ┌───────────── seconds (0 - 59)
    │ ┌───────────── minute (0 - 59)
    │ │ ┌───────────── hour (0 - 23)
    │ │ │ ┌───────────── day of month (1 - 31)
    │ │ │ │ ┌───────────── month (1 - 12)
    │ │ │ │ │ ┌───────────── day of week (0 - 6) (Sunday to Saturday; 7 is also Sunday on some systems)
    │ │ │ │ │ │
    │ │ │ │ │ │
    │ │ │ │ │ │
    * * * * * *  command to execute
    */
    const month = date.getMonth() + 1;
    const day = date.getDate();

    switch (type) {
        case ScheduleFrequency.Once:
            return `0 ${minute} ${hour} ${day} ${month} *`;
        case ScheduleFrequency.Weekly:
            if (!daysOfWeek || daysOfWeek.length === 0) {
                return "";
            }
            return `0 ${minute} ${hour} * * ${daysOfWeek.toString()}`;
        case ScheduleFrequency.Monthly:
            if (!daysOfMonth || daysOfMonth.length === 0) {
                return "";
            }
            return `0 ${minute} ${hour} ${daysOfMonth.toString()} * *`;
        case ScheduleFrequency.Periodically:
            return parsePeriodic(minute, hour, day, month, periodic);
        default:
            console.log("Unknown schedule type");
            return "";
    }
}
// seconds, minutes, hours, day of month, month, day of week, year
function parsePeriodic(
    minute: string,
    hour: string,
    day: number,
    month: number,
    periodic: { name: any; value: any }
): string {
    if (!periodic || !periodic.value) {
        return "";
    }
    const interval = periodic.value;

    switch (periodic.name) {
        case "minute":
            return `0 ${calculateMinutes(+minute, interval)} * * * * `;
        case "hour":
            return `0 ${minute} ${calculateHours(+hour, interval)} * * * `;
        case "month":
            return `0 ${minute} ${hour} ${day} */${interval} *`;

        //! periodic schedules of type daily and weekly ignore the start day
        //! and instead start on first of the month as there isn't really a way
        //! to have to do this with cron.
        case "day":
            return `0 ${minute} ${hour} */${interval} * *`;
        case "week":
            return `0 ${minute} ${hour} */${interval * 7} * *`;

        default:
            console.error("Unknown periodic type");
            return "";
    }
}

export const dateComparator = (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime();

export function navigateToNextCell(params: NavigateToNextCellParams) {
    if (!params.nextCellPosition) {
        return params.previousCellPosition;
    }

    const gridApi = (params.nextCellPosition.column as any).gridApi as GridApi;

    if (gridApi) {
        const prevCell = params.previousCellPosition;
        const nextCell = params.nextCellPosition;

        const shiftPressed = params.event!.shiftKey;

        if (nextCell.rowIndex !== prevCell.rowIndex) {
            gridApi.forEachNodeAfterFilterAndSort((node) => {
                if (nextCell.rowIndex === node.rowIndex) {
                    node.setSelected(true);
                } else if (!shiftPressed) {
                    node.setSelected(false);
                }
            });
        }
    }

    return params.nextCellPosition;
}

export function generateNumbers(toNumber: number, fromNum: number = 1, increment: number = 1) {
    const arr: number[] = [];
    for (let i = fromNum; i <= toNumber; i += increment) {
        arr.push(i);
    }
    return arr;
}

export function getReadableCronExpression(cron: string, startTime: string, startDate: Date): string {
    const expression = cronstrue.toString(cron);
    const isMinute = cron.split(" ")[1].includes("/");
    const addStartTimeStr = isMinute ? ` at ${startTime},` : "";
    if (expression && startTime && startDate) {
        return `"Starting on ${startDate.toDateString()},${addStartTimeStr} run ${expression.replace("At", "at")}"`;
    }
    return "Invalid Selection. Please Fill Out Start Time and Start Date Fields";
}

export const getFormikFieldName = (n: string) => `user_inputs.${n}`;
export const getFormikFieldValueReference = (n: string) => `${getFormikFieldName(n)}.value`;
export const getFormikFieldNameReference = (n: string) => `${getFormikFieldName(n)}.name`;
export const formikCompanyFieldName = "company";

export function createScheduleJobObject(
    state: NewScheduleState,
    project: Project,
    scheduleId?: string
): ScheduleInfoInput {
    const {
        startTime,
        startDate,
        scheduleType,
        daysOfWeek,
        daysOfMonth,
        periodic,
        company,
        user_inputs,
        active,
        cron,
        interval,
        scheduleName,
        timeZone
    } = {
        ...state
    };

    const [hours, minutes] = startTime.split(":");
    const withTime = new Date(startDate);
    withTime.setHours(parseInt(hours));
    withTime.setMinutes(parseInt(minutes));
    let addDataValue = undefined;
    // set additional data on the schedule to be eble to recreate state when users request to edit variables
    switch (scheduleType) {
        case "Weekly":
            addDataValue = daysOfWeek;
            break;
        case "Monthly":
            addDataValue = daysOfMonth;
            break;
        case "Periodically":
            addDataValue = periodic.value;
            break;
    }
    // add this to transform old schedules with specialCompanyField
    const inputs = user_inputs;
    if (inputs[specialCompanyField] && !inputs[specialCompanyField].value) {
        inputs[specialCompanyField].value = company.id as string;
        inputs[specialCompanyField].name = company.name as string;
    }
    return {
        id: scheduleId,
        company: { name: company.name, sysId: company.id },
        project: { name: project.name, id: project.projectId },
        user_input: user_inputs,
        active: active,
        cron: cron,
        schedule: {
            frequency: scheduleType,
            interval: interval,
            schedName: scheduleName,
            startTime: withTime.toISOString(),
            timeZone: timeZone,
            additionalInfo: {
                scheduleType: scheduleType,
                subType: periodic.name,
                value: addDataValue
            }
        }
    };
}

const MS_IN_MINUTE = 1000 * 60;

export function getTimeRange(t: number): TimeRangeOptions {
    const now = Date.now();
    const minutesSince = Math.abs(Math.ceil((now - t) / MS_IN_MINUTE));
    const timeRange = timeWindowOptions.find((r) => r.value - minutesSince >= 0);
    return timeRange ? timeRange : getTimeRangeCustom(t);
}

export function getTimeRangeCustom(t: number | Date) {
    return { key: "custom", text: `Since ${shortDate(t)}`, value: typeof t === "number" ? t : t.getTime() };
}

const pattern = "LLLL dd, yyyy  hh:mm:ss aaa";
export const localTime = (d: number | Date) => format(d, pattern);
export function getZonedTime(d: string, timeZone: string) {
    const date = new Date(d);
    const zonedDate = utcToZonedTime(date, timeZone);
    return format(zonedDate, pattern, { timeZone });
}
export const shortDate = (d: number | Date) => format(d, "LLLL dd, yyyy");

function getMinuteHoursFromCron(cron: string) {
    const t = cron.split(" ");
    const m = t[1].includes("/") ? t[1].split("/")[1] : t[1];
    const h = t[2];
    return m === "*" ? "23:59" : `${h}:${m}`;
}

export function createFromExisting(
    existing: ScheduleDoc,
    projectInputs?: { [key: string]: any },
    clone: boolean = false
): NewScheduleState {
    const { cron, active, schedule, company, user_input } = existing;
    const schedType = schedule.additionalInfo.scheduleType;
    const value = schedule.additionalInfo.value;
    const startDate = new Date(schedule.startTime);
    const hours = startDate.getHours();
    let minutes = startDate.getMinutes().toString();
    minutes = minutes.length === 1 ? `0${minutes}` : minutes;
    // !todo: verify that this does not create issues when using Periodic schedules
    const scheduleTime = getMinuteHoursFromCron(cron);

    const user_inputs = setInitialUserInputs(projectInputs, user_input);
    if (user_inputs[specialCompanyField] && !user_inputs[specialCompanyField].value && schedule) {
        user_inputs[specialCompanyField].value = existing.company.sysId as string;
        user_inputs[specialCompanyField].name = existing.company.name as string;
    }
    const o: NewScheduleState = {
        cron: cron,
        company: { name: clone ? "" : company.name, id: clone ? "" : company.sysId, ops: false },
        scheduleName: clone ? "" : schedule.schedName,
        scheduleType: schedType,
        startDate: startDate,
        startTime: `${hours}:${minutes}`,
        scheduleTime: scheduleTime,
        daysOfWeek: schedType === "Weekly" ? (value as number[]) : [],
        daysOfMonth: schedType === "Monthly" ? (value as number[]) : [],
        interval: 1,
        periodic:
            schedType === "Periodically"
                ? {
                      name: schedule.additionalInfo.subType as PeriodicSubType,
                      value: value as number
                  }
                : { name: undefined, value: undefined },
        timeZone: schedule.timeZone,
        user_inputs: !clone ? setInitialUserInputs(projectInputs, user_input) : setInitialUserInputs(user_input),
        active: active
    };
    console.log("reconsturcted: ", o);
    return o;
}

// function getUserInputValidationSchema(userInputs: { [key: string]: ProjectVariable } | undefined) {
//     const temp: { [key: string]: any } = {};
//     if (userInputs) {
//         for (const [name, input] of Object.entries(userInputs)) {
//             // ignore special company field
//             if (input.required && name !== specialCompanyField) {
//                 temp[name] = object().shape({
//                     value: string().required(`${input.label} is a required field`)
//                 });
//             }
//         }
//     }
//     return object().shape({
//         user_inputs: object().shape(temp)
//     });
// }
